import React, { useEffect, useMemo, useState } from 'react';
import {
    CalendarDays,
    ClipboardPlus,
    Globe2,
    ListChecks,
    X,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../ui/card';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Combobox } from '../ui/combobox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import {
    checkFeedbackSurveyOverlaps,
    createFeedbackSurvey,
    deleteFeedbackSurvey,
    FeedbackSurvey,
    FeedbackSurveyOptions,
    FeedbackSurveyOverlapResult,
    FeedbackSurveyScope,
    FeedbackSurveyTemplate,
    getFeedbackSurveyOptions,
    getFeedbackSurveys,
    updateFeedbackSurvey,
} from '../../services/api/feedbackSurveyService';
import { cn } from '../../lib/utils';
import { getActorDisplayName } from '../../utils/roleUtils';

type SurveyMode = 'trial' | 'custom' | 'overview' | null;
type OverviewTab = 'trial' | 'custom';
type ComboboxOption = { value: string; label: string };

interface TrialTemplateSetup {
    startDate: string;
    durationDays: string;
}

interface SurveyEditDraft {
    title: string;
    description: string;
    formId: string;
    startDate: string;
    durationDays: string;
    includeTrialSites: boolean;
}

type SurveyRunStatus = 'active' | 'scheduled' | 'expired' | 'inactive';

interface PendingOverlapAction {
    overlap: FeedbackSurveyOverlapResult;
    onProceed: () => Promise<void>;
}

const ALL_DEPARTMENTS_VALUE = '__all_departments__';
const ALL_ROLES_VALUE = '__all_roles__';
const ALL_REGIONS_VALUE = '__all_regions__';

const scopeLabels: Record<FeedbackSurveyScope, string> = {
    trial: 'Trial',
    subscription: 'Subscription',
    global: 'Global',
};

const todayString = () => {
    const today = new Date();
    const timezoneOffsetMs = today.getTimezoneOffset() * 60 * 1000;
    return new Date(today.getTime() - timezoneOffsetMs)
        .toISOString()
        .slice(0, 10);
};

const isPastDate = (date?: string) => Boolean(date && date < todayString());

const getEndDate = (startDate: string, durationDays: string) => {
    const duration = Number(durationDays);

    if (!startDate || !duration || duration < 1) {
        return '';
    }

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + duration);
    return endDate.toISOString().slice(0, 10);
};

const formatDate = (date?: string) => date?.slice(0, 10) ?? '-';

const getSurveyDepartmentIds = (survey?: FeedbackSurvey) => {
    if (!survey) {
        return [];
    }

    return survey.department_ids?.map((department) => department._id) ?? [];
};

const getPrimarySurveyDepartmentId = (survey?: FeedbackSurvey) => {
    return getSurveyDepartmentIds(survey)[0];
};

const surveyIncludesTrialSites = (survey: FeedbackSurvey) =>
    survey.scope === 'trial' ||
    (survey.scope === 'global' && survey.includeTrialSites === true);

const surveyIncludesSubscriptionSites = (survey: FeedbackSurvey) =>
    survey.scope === 'subscription' ||
    survey.scope === 'global';

const departmentMatchesSurveyScope = (
    department: FeedbackSurveyOptions['departments'][number],
    survey: FeedbackSurvey
) => {
    return department.isTrial
        ? surveyIncludesTrialSites(survey)
        : surveyIncludesSubscriptionSites(survey);
};

const getSurveyRunStatus = (survey?: FeedbackSurvey): SurveyRunStatus | null => {
    if (!survey) {
        return null;
    }

    const now = new Date();
    const startDate = survey.startDate ? new Date(survey.startDate) : null;
    const endDate = survey.endDate ? new Date(survey.endDate) : null;

    if (!survey.isActive) {
        return 'inactive';
    }

    if (startDate && startDate > now) {
        return 'scheduled';
    }

    if (endDate && endDate < now) {
        return 'expired';
    }

    return 'active';
};

const surveyStatusLabels: Record<SurveyRunStatus, string> = {
    active: 'Active',
    scheduled: 'Scheduled',
    expired: 'Completed',
    inactive: 'Inactive',
};

const requiredInputClassName = 'border-primary/60 focus-visible:ring-primary';

const FeedbackSurveySetup: React.FC = () => {
    const { toast } = useToast();
    const [mode, setMode] = useState<SurveyMode>(null);
    const [options, setOptions] = useState<FeedbackSurveyOptions | null>(null);
    const [surveys, setSurveys] = useState<FeedbackSurvey[]>([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [savingKey, setSavingKey] = useState<string | null>(null);

    const [trialDepartmentId, setTrialDepartmentId] = useState('');
    const [trialTemplateSetup, setTrialTemplateSetup] = useState<
        Record<string, TrialTemplateSetup>
    >({});

    const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<
        string[]
    >([]);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
    const [allSitesSelected, setAllSitesSelected] = useState(false);
    const [customTitle, setCustomTitle] = useState('');
    const [customDescription, setCustomDescription] = useState('');
    const [customStartDate, setCustomStartDate] = useState(todayString());
    const [customDurationDays, setCustomDurationDays] = useState('');
    const [customFormId, setCustomFormId] = useState('');
    const [overviewTab, setOverviewTab] = useState<OverviewTab>('trial');
    const [overviewDepartmentFilter, setOverviewDepartmentFilter] =
        useState('all');
    const [overviewRoleFilter, setOverviewRoleFilter] = useState('all');
    const [overviewRegionFilter, setOverviewRegionFilter] = useState('all');
    const [overviewTrialDepartmentId, setOverviewTrialDepartmentId] =
        useState('');
    const [surveyDrafts, setSurveyDrafts] = useState<
        Record<string, SurveyEditDraft>
    >({});
    const [editingSurveyId, setEditingSurveyId] = useState<string | null>(null);
    const [pendingDeleteSurvey, setPendingDeleteSurvey] =
        useState<FeedbackSurvey | null>(null);
    const [pendingOverlapAction, setPendingOverlapAction] =
        useState<PendingOverlapAction | null>(null);

    const fetchSurveys = async () => {
        const data = await getFeedbackSurveys();
        setSurveys(data);
    };

    useEffect(() => {
        setLoadingOptions(true);
        Promise.all([getFeedbackSurveyOptions(), getFeedbackSurveys()])
            .then(([optionData, surveyData]) => {
                setOptions(optionData);
                setSurveys(surveyData);
                const setup = optionData.defaultTemplates.reduce(
                    (acc, template) => ({
                        ...acc,
                        [template.templateKey]: {
                            startDate: todayString(),
                            durationDays: '',
                        },
                    }),
                    {} as Record<string, TrialTemplateSetup>
                );
                setTrialTemplateSetup(setup);
            })
            .catch(() => {
                toast({
                    title: 'Failed to load survey options.',
                    variant: 'destructive',
                });
            })
            .finally(() => setLoadingOptions(false));
    }, [toast]);

    const getLatestTrialSurvey = (templateKey: string) => {
        if (!trialDepartmentId) {
            return undefined;
        }

        return surveys.find(
            (survey) =>
                survey.isDefault &&
                survey.templateKey === templateKey &&
                getPrimarySurveyDepartmentId(survey) === trialDepartmentId
        );
    };

    useEffect(() => {
        if (!options || !trialDepartmentId) {
            return;
        }

        setTrialTemplateSetup((current) => {
            const next = { ...current };

            options.defaultTemplates.forEach((template) => {
                const latestSurvey = surveys.find(
                    (survey) =>
                        survey.isDefault &&
                        survey.templateKey === template.templateKey &&
                        getPrimarySurveyDepartmentId(survey) === trialDepartmentId
                );

                next[template.templateKey] = {
                    startDate:
                        latestSurvey?.startDate?.slice(0, 10) ??
                        current[template.templateKey]?.startDate ??
                        todayString(),
                    durationDays:
                        latestSurvey?.durationDays?.toString() ??
                        current[template.templateKey]?.durationDays ??
                        '',
                };
            });

            return next;
        });
    }, [options, surveys, trialDepartmentId]);

    const availableDepartments = useMemo(
        () =>
            (options?.departments ?? []).filter(
                (department) =>
                    (selectedRegions.length === 0 ||
                        (department.region !== undefined &&
                            selectedRegions.includes(department.region)))
            ),
        [options?.departments, selectedRegions]
    );

    const trialDepartments = useMemo(
        () =>
            (options?.departments ?? []).filter(
                (department) => department.isTrial
            ),
        [options?.departments]
    );

    useEffect(() => {
        setSelectedDepartmentIds((current) =>
            current.filter((id) =>
                availableDepartments.some((department) => department._id === id)
            )
        );
    }, [availableDepartments]);

    useEffect(() => {
        if (selectedDepartmentIds.length === 0) {
            return;
        }

        const selectedDepartmentRegions = new Set(
            (options?.departments ?? [])
                .filter((department) =>
                    selectedDepartmentIds.includes(department._id)
                )
                .map((department) => department.region)
                .filter(Boolean)
        );

        setSelectedRegions((current) =>
            current.filter((region) => selectedDepartmentRegions.has(region))
        );
    }, [options?.departments, selectedDepartmentIds]);

    useEffect(() => {
        setSurveyDrafts((current) => {
            const next = { ...current };

            surveys.forEach((survey) => {
                if (!next[survey._id]) {
                    next[survey._id] = {
                        title: survey.title,
                        description: survey.description ?? '',
                        formId: survey.formId,
                        startDate: survey.startDate?.slice(0, 10) ?? '',
                        durationDays: survey.durationDays?.toString() ?? '',
                        includeTrialSites: Boolean(survey.includeTrialSites),
                    };
                }
            });

            return next;
        });
    }, [surveys]);

    const closePanel = () => {
        setMode(null);
    };

    const resetCustomForm = () => {
        setSelectedDepartmentIds([]);
        setSelectedRoles([]);
        setSelectedRegions([]);
        setAllSitesSelected(false);
        setCustomTitle('');
        setCustomDescription('');
        setCustomStartDate(todayString());
        setCustomDurationDays('');
        setCustomFormId('');
    };

    const addUnique = (
        value: string,
        values: string[],
        setValues: React.Dispatch<React.SetStateAction<string[]>>
    ) => {
        if (value && !values.includes(value)) {
            setValues([...values, value]);
        }
    };

    const removeValue = (
        value: string,
        values: string[],
        setValues: React.Dispatch<React.SetStateAction<string[]>>
    ) => {
        setValues(values.filter((item) => item !== value));
    };

    const selectDepartment = (value: string) => {
        if (value === ALL_DEPARTMENTS_VALUE) {
            setAllSitesSelected(true);
            setSelectedDepartmentIds([]);
            return;
        }

        setAllSitesSelected(false);
        addUnique(value, selectedDepartmentIds, setSelectedDepartmentIds);
    };

    const selectRole = (value: string) => {
        if (value === ALL_ROLES_VALUE) {
            setSelectedRoles((options?.roles ?? []).map((role) => role.role));
            return;
        }

        addUnique(value, selectedRoles, setSelectedRoles);
    };

    const selectRegion = (value: string) => {
        if (value === ALL_REGIONS_VALUE) {
            setSelectedRegions(targetingRegionOptions
                .filter((region) => region.value !== ALL_REGIONS_VALUE)
                .map((region) => region.value)
            );
            return;
        }

        addUnique(value, selectedRegions, setSelectedRegions);
    };

    const getOverlapFromError = (error: any) => {
        return error?.details?.overlap as
            | FeedbackSurveyOverlapResult
            | undefined;
    };

    const showOverlapWarning = (
        overlap: FeedbackSurveyOverlapResult,
        onProceed: () => Promise<void>
    ) => {
        setPendingOverlapAction({
            overlap,
            onProceed,
        });
    };

    const updateTrialTemplateSetup = (
        templateKey: string,
        field: keyof TrialTemplateSetup,
        value: string
    ) => {
        setTrialTemplateSetup((prev) => ({
            ...prev,
            [templateKey]: {
                ...prev[templateKey],
                [field]: value,
            },
        }));
    };

    const saveTrialSurvey = async (
        template: FeedbackSurveyTemplate,
        allowOverlap = false
    ) => {
        const setup = trialTemplateSetup[template.templateKey];
        const durationDays = Number(setup?.durationDays);
        const latestSurvey = getLatestTrialSurvey(template.templateKey);
        const status = getSurveyRunStatus(latestSurvey);
        const payload = {
            templateKey: template.templateKey,
            department_ids: [trialDepartmentId],
            startDate: setup.startDate,
            durationDays,
            isActive: true,
            allowOverlap,
        };

        if (latestSurvey && (status === 'active' || status === 'scheduled')) {
            await updateFeedbackSurvey(latestSurvey._id, {
                startDate: setup.startDate,
                durationDays,
                isActive: true,
                allowOverlap,
            });
        } else {
            await createFeedbackSurvey(payload);
        }

        await fetchSurveys();
        toast({
            title:
                latestSurvey && status !== 'expired'
                    ? `${template.title} updated.`
                    : `${template.title} activated.`,
        });
    };

    const handleActivateTrialSurvey = async (
        template: FeedbackSurveyTemplate
    ) => {
        const setup = trialTemplateSetup[template.templateKey];
        const durationDays = Number(setup?.durationDays);
        const latestSurvey = getLatestTrialSurvey(template.templateKey);
        const status = getSurveyRunStatus(latestSurvey);

        if (!trialDepartmentId) {
            toast({
                title: 'Choose a department before activating a trial survey.',
                variant: 'destructive',
            });
            return;
        }

        if (
            !setup?.startDate ||
            isPastDate(setup.startDate) ||
            !durationDays ||
            durationDays < 1
        ) {
            toast({
                title:
                    'Set a valid start date from today or later and duration.',
                variant: 'destructive',
            });
            return;
        }

        setSavingKey(template.templateKey);
        try {
            const overlap = await checkFeedbackSurveyOverlaps({
                templateKey: template.templateKey,
                scope: 'trial',
                department_ids: [trialDepartmentId],
                includeTrialSites: true,
                startDate: setup.startDate,
                durationDays,
                isActive: true,
                excludeSurveyId:
                    latestSurvey &&
                    (status === 'active' || status === 'scheduled')
                        ? latestSurvey._id
                        : undefined,
            });

            if (overlap.hasOverlap) {
                showOverlapWarning(overlap, () =>
                    saveTrialSurvey(template, true)
                );
            } else {
                await saveTrialSurvey(template);
            }
        } catch (error: any) {
            const overlap = getOverlapFromError(error);

            if (overlap?.hasOverlap) {
                showOverlapWarning(overlap, () =>
                    saveTrialSurvey(template, true)
                );
                return;
            }

            toast({
                title: 'Failed to save default trial survey.',
                description: error?.message,
                variant: 'destructive',
            });
        } finally {
            setSavingKey(null);
        }
    };

    const handleDeactivateTrialSurvey = async (survey: FeedbackSurvey) => {
        setSavingKey(`${survey._id}:deactivate`);
        try {
            await updateFeedbackSurvey(survey._id, {
                isActive: false,
            });
            await fetchSurveys();
            toast({ title: `${survey.title} deactivated.` });
        } catch (error: any) {
            toast({
                title: 'Failed to deactivate default trial survey.',
                description: error?.data?.message,
                variant: 'destructive',
            });
        } finally {
            setSavingKey(null);
        }
    };

    const updateSurveyDraft = (
        surveyId: string,
        field: keyof SurveyEditDraft,
        value: string | boolean
    ) => {
        setSurveyDrafts((current) => ({
            ...current,
            [surveyId]: {
                ...current[surveyId],
                [field]: value,
            },
        }));
    };

    const saveSurveyDraft = async (
        survey: FeedbackSurvey,
        allowOverlap = false
    ) => {
        const draft = surveyDrafts[survey._id];
        const durationDays = Number(draft?.durationDays);

        await updateFeedbackSurvey(survey._id, {
            title: survey.isDefault ? undefined : draft.title.trim(),
            description: survey.isDefault
                ? undefined
                : draft.description.trim(),
            formId: survey.isDefault ? undefined : draft.formId.trim(),
            startDate: draft.startDate,
            durationDays,
            includeTrialSites: draft.includeTrialSites,
            isActive: true,
            allowOverlap,
        });
        await fetchSurveys();
        setEditingSurveyId(null);
        toast({ title: `${survey.title} updated.` });
    };

    const handleSaveSurveyDraft = async (survey: FeedbackSurvey) => {
        const draft = surveyDrafts[survey._id];
        const durationDays = Number(draft?.durationDays);

        if (
            !draft?.startDate ||
            isPastDate(draft.startDate) ||
            !durationDays ||
            durationDays < 1
        ) {
            toast({
                title:
                    'Set a valid start date from today or later and duration.',
                variant: 'destructive',
            });
            return;
        }

        if (!survey.isDefault && (!draft.title.trim() || !draft.formId.trim())) {
            toast({
                title: 'Name and Tally form ID are required.',
                variant: 'destructive',
            });
            return;
        }

        setSavingKey(`${survey._id}:save`);
        try {
            const overlap = await checkFeedbackSurveyOverlaps({
                title: survey.isDefault ? survey.title : draft.title.trim(),
                description: survey.isDefault
                    ? survey.description
                    : draft.description.trim(),
                formId: survey.isDefault ? survey.formId : draft.formId.trim(),
                scope: survey.scope,
                allSites: survey.allSites,
                department_ids: getSurveyDepartmentIds(survey),
                roles: survey.roles,
                regions: survey.regions,
                includeTrialSites: draft.includeTrialSites,
                startDate: draft.startDate,
                durationDays,
                isActive: true,
                excludeSurveyId: survey._id,
            });

            if (overlap.hasOverlap) {
                showOverlapWarning(overlap, () => saveSurveyDraft(survey, true));
            } else {
                await saveSurveyDraft(survey);
            }
        } catch (error: any) {
            const overlap = getOverlapFromError(error);

            if (overlap?.hasOverlap) {
                showOverlapWarning(overlap, () => saveSurveyDraft(survey, true));
                return;
            }

            toast({
                title: 'Failed to update survey.',
                description: error?.message,
                variant: 'destructive',
            });
        } finally {
            setSavingKey(null);
        }
    };

    const handleDeactivateSurvey = async (survey: FeedbackSurvey) => {
        setPendingDeleteSurvey(survey);
    };

    const handleConfirmDeleteSurvey = async () => {
        if (!pendingDeleteSurvey) {
            return;
        }

        const survey = pendingDeleteSurvey;
        setSavingKey(`${survey._id}:deactivate`);
        try {
            await deleteFeedbackSurvey(survey._id);
            await fetchSurveys();
            setEditingSurveyId((current) =>
                current === survey._id ? null : current
            );
            setPendingDeleteSurvey(null);
            toast({ title: `${survey.title} deleted.` });
        } catch (error: any) {
            toast({
                title: 'Failed to delete survey.',
                description: error?.data?.message,
                variant: 'destructive',
            });
        } finally {
            setSavingKey(null);
        }
    };

    const handleProceedWithOverlap = async () => {
        if (!pendingOverlapAction) {
            return;
        }

        const action = pendingOverlapAction;
        setSavingKey('overlap');
        try {
            await action.onProceed();
            setPendingOverlapAction(null);
        } catch (error: any) {
            toast({
                title: 'Failed to save survey.',
                description: error?.message,
                variant: 'destructive',
            });
        } finally {
            setSavingKey(null);
        }
    };

    const getCustomSurveyPayload = (allowOverlap = false) => {
        const durationDays = Number(customDurationDays);

        return {
            title: customTitle.trim(),
            description: customDescription.trim(),
            formId: customFormId.trim(),
            scope: 'global' as const,
            allSites: allSitesSelected,
            department_ids: allSitesSelected ? [] : selectedDepartmentIds,
            roles: selectedRoles,
            regions: selectedRegions,
            includeTrialSites: true,
            startDate: customStartDate,
            durationDays,
            isActive: true,
            allowOverlap,
        };
    };

    const saveCustomSurvey = async (allowOverlap = false) => {
        await createFeedbackSurvey(getCustomSurveyPayload(allowOverlap));
        await fetchSurveys();
        toast({ title: 'Survey created and activated.' });
        resetCustomForm();
    };

    const handleCreateCustomSurvey = async () => {
        const durationDays = Number(customDurationDays);
        const departmentIds = allSitesSelected ? [] : selectedDepartmentIds;

        if (
            !allSitesSelected &&
            departmentIds.length === 0 &&
            selectedRoles.length === 0 &&
            selectedRegions.length === 0
        ) {
            toast({
                title: 'Choose at least one target.',
                variant: 'destructive',
            });
            return;
        }

        if (!customTitle.trim() || !customFormId.trim()) {
            toast({
                title: 'Name and Tally form ID are required.',
                variant: 'destructive',
            });
            return;
        }

        if (
            !customStartDate ||
            isPastDate(customStartDate) ||
            !durationDays ||
            durationDays < 1
        ) {
            toast({
                title:
                    'Set a valid start date from today or later and duration.',
                variant: 'destructive',
            });
            return;
        }

        setSavingKey('custom');
        try {
            const overlap = await checkFeedbackSurveyOverlaps(
                getCustomSurveyPayload()
            );

            if (overlap.hasOverlap) {
                showOverlapWarning(overlap, () => saveCustomSurvey(true));
            } else {
                await saveCustomSurvey();
            }
        } catch (error: any) {
            const overlap = getOverlapFromError(error);

            if (overlap?.hasOverlap) {
                showOverlapWarning(overlap, () => saveCustomSurvey(true));
                return;
            }

            toast({
                title: 'Failed to create survey.',
                description: error?.message,
                variant: 'destructive',
            });
        } finally {
            setSavingKey(null);
        }
    };

    const renderSelectedPills = (
        values: string[],
        labels: Record<string, string>,
        onRemove: (value: string) => void
    ) => (
        <div className="flex flex-wrap gap-2">
            {values.map((value) => (
                <span
                    key={value}
                    className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs"
                >
                    {labels[value] ?? value}
                    <button
                        type="button"
                        onClick={() => onRemove(value)}
                        aria-label="Remove"
                    >
                        <X className="h-3 w-3" />
                    </button>
                </span>
            ))}
        </div>
    );

    const departmentLabels = useMemo(
        () =>
            (options?.departments ?? []).reduce((acc, department) => {
                acc[department._id] = department.name;
                return acc;
            }, {} as Record<string, string>),
        [options?.departments]
    );

    const roleLabels = useMemo(
        () =>
            (options?.roles ?? []).reduce((acc, role) => {
                acc[role.role] = getActorDisplayName(role);
                return acc;
            }, {} as Record<string, string>),
        [options?.roles]
    );

    const regionLabels = useMemo(
        () =>
            (options?.regions ?? []).reduce((acc, region) => {
                acc[region] = region;
                return acc;
            }, {} as Record<string, string>),
        [options?.regions]
    );

    const departmentOptions = useMemo<ComboboxOption[]>(
        () =>
            (options?.departments ?? []).map((department) => ({
                value: department._id,
                label: department.name,
            })),
        [options?.departments]
    );

    const availableDepartmentOptions = useMemo<ComboboxOption[]>(
        () => [
            { value: ALL_DEPARTMENTS_VALUE, label: 'All departments' },
            ...availableDepartments.map((department) => ({
                value: department._id,
                label: `${department.name}${department.isTrial ? ' (Trial)' : ''}`,
            })),
        ],
        [availableDepartments]
    );

    const trialDepartmentOptions = useMemo<ComboboxOption[]>(
        () =>
            trialDepartments.map((department) => ({
                value: department._id,
                label: `${department.name}${
                    department.region ? ` (${department.region})` : ''
                }`,
            })),
        [trialDepartments]
    );

    const roleOptions = useMemo<ComboboxOption[]>(
        () => [
            { value: ALL_ROLES_VALUE, label: 'All roles' },
            ...(options?.roles ?? []).map((role) => ({
                value: role.role,
                label: getActorDisplayName(role),
            })),
        ],
        [options?.roles]
    );

    const regionOptions = useMemo<ComboboxOption[]>(
        () => [
            { value: ALL_REGIONS_VALUE, label: 'All regions' },
            ...(options?.regions ?? []).map((region) => ({
                value: region,
                label: region,
            })),
        ],
        [options?.regions]
    );

    const targetingRegionOptions = useMemo<ComboboxOption[]>(
        () => {
            if (selectedDepartmentIds.length === 0) {
                return regionOptions;
            }

            const selectedDepartmentRegions = new Set(
                (options?.departments ?? [])
                    .filter((department) =>
                        selectedDepartmentIds.includes(department._id)
                    )
                    .map((department) => department.region)
                    .filter(Boolean)
            );

            return regionOptions.filter(
                (region) =>
                    region.value === ALL_REGIONS_VALUE ||
                    selectedDepartmentRegions.has(region.value)
            );
        },
        [options?.departments, regionOptions, selectedDepartmentIds]
    );

    const activeManageableSurveys = useMemo(
        () =>
            surveys.filter((survey) => {
                const status = getSurveyRunStatus(survey);
                return status === 'active' || status === 'scheduled';
            }),
        [surveys]
    );

    const activeCustomSurveys = useMemo(
        () =>
            activeManageableSurveys.filter(
                (survey) => !survey.isDefault
            ),
        [activeManageableSurveys]
    );

    const activeTrialSurveys = useMemo(
        () =>
            activeManageableSurveys.filter(
                (survey) => {
                    const departmentId = getPrimarySurveyDepartmentId(survey);
                    const department = options?.departments.find(
                        (item) => item._id === departmentId
                    );

                    return (
                        survey.scope === 'trial' &&
                        survey.isDefault &&
                        Boolean(departmentId) &&
                        Boolean(department?.isTrial)
                    );
                }
            ),
        [activeManageableSurveys, options?.departments]
    );

    const activeTrialDepartments = useMemo(() => {
        const departments = activeTrialSurveys.reduce((acc, survey) => {
            const departmentId = getPrimarySurveyDepartmentId(survey);
            if (departmentId && !acc.some((department) => department._id === departmentId)) {
                const department = options?.departments.find(
                    (item) => item._id === departmentId
                );
                if (department) {
                    acc.push(department);
                }
            }
            return acc;
        }, [] as NonNullable<FeedbackSurveyOptions['departments']>);

        return departments.sort((a, b) => a.name.localeCompare(b.name));
    }, [activeTrialSurveys, options?.departments]);

    const filteredCustomSurveys = useMemo(() => {
        return activeCustomSurveys.filter((survey) => {
            const departmentIds = getSurveyDepartmentIds(survey);
            const selectedDepartment =
                overviewDepartmentFilter === 'all'
                    ? undefined
                    : options?.departments.find(
                          (department) =>
                              department._id === overviewDepartmentFilter
                      );
            const allSitesDepartmentMatch =
                survey.allSites &&
                selectedDepartment &&
                departmentMatchesSurveyScope(selectedDepartment, survey) &&
                (!survey.regions?.length ||
                    (selectedDepartment.region !== undefined &&
                        survey.regions.includes(selectedDepartment.region)));
            const departmentMatch =
                overviewDepartmentFilter === 'all' ||
                allSitesDepartmentMatch ||
                departmentIds.includes(overviewDepartmentFilter);
            const roleMatch =
                overviewRoleFilter === 'all' ||
                survey.roles?.includes(overviewRoleFilter);
            const regionMatch =
                overviewRegionFilter === 'all' ||
                survey.regions?.includes(overviewRegionFilter);

            return departmentMatch && roleMatch && regionMatch;
        });
    }, [
        activeCustomSurveys,
        overviewDepartmentFilter,
        overviewRegionFilter,
        overviewRoleFilter,
        options?.departments,
    ]);

    useEffect(() => {
        if (
            overviewTrialDepartmentId &&
            activeTrialDepartments.some(
                (department) => department._id === overviewTrialDepartmentId
            )
        ) {
            return;
        }

        setOverviewTrialDepartmentId(activeTrialDepartments[0]?._id ?? '');
    }, [activeTrialDepartments, overviewTrialDepartmentId]);

    const surveyComboboxProps = {
        contentClassName: 'z-[1000] overflow-hidden',
        listStyle: {
            maxHeight: 240,
            overflowY: 'auto' as const,
            overscrollBehavior: 'contain' as const,
            scrollbarWidth: 'thin' as const,
        },
        scrollableOptions: true,
    };

    const renderDepartmentDropdown = () => (
        <div className="grid gap-2">
            <label className="text-xs font-medium">Departments</label>
            <Combobox
                {...surveyComboboxProps}
                options={availableDepartmentOptions}
                value=""
                onChange={selectDepartment}
                placeholder="Search department"
                emptyText="No departments found."
            />
            {allSitesSelected &&
                renderSelectedPills(
                    [ALL_DEPARTMENTS_VALUE],
                    { [ALL_DEPARTMENTS_VALUE]: 'All departments' },
                    () => setAllSitesSelected(false)
                )}
            {renderSelectedPills(
                selectedDepartmentIds,
                departmentLabels,
                (value) =>
                    removeValue(
                        value,
                        selectedDepartmentIds,
                        setSelectedDepartmentIds
                    )
            )}
        </div>
    );

    const renderRoleDropdown = () => (
        <div className="grid gap-2">
            <label className="text-xs font-medium">Roles</label>
            <Combobox
                {...surveyComboboxProps}
                options={roleOptions}
                value=""
                onChange={selectRole}
                placeholder="Search role"
                emptyText="No roles found."
            />
            {renderSelectedPills(selectedRoles, roleLabels, (value) =>
                removeValue(value, selectedRoles, setSelectedRoles)
            )}
        </div>
    );

    const renderRegionDropdown = () => (
        <div className="grid gap-2">
            <label className="text-xs font-medium">Regions</label>
            <Combobox
                {...surveyComboboxProps}
                options={targetingRegionOptions}
                value=""
                onChange={selectRegion}
                placeholder="Search region"
                emptyText="No regions found."
            />
            {renderSelectedPills(selectedRegions, regionLabels, (value) =>
                removeValue(value, selectedRegions, setSelectedRegions)
            )}
        </div>
    );

    const renderSurveyTargetSummary = (survey: FeedbackSurvey) => {
        const departmentIds = getSurveyDepartmentIds(survey);
        const departments =
            departmentIds.length > 0
                ? departmentIds
                      .map((id) => departmentLabels[id])
                      .filter(Boolean)
                      .join(', ')
                : survey.allSites
                ? 'All departments'
                : 'No department selected';
        const roles = survey.roles?.length ? survey.roles.join(', ') : 'Any role';
        const regions = survey.regions?.length
            ? survey.regions.join(', ')
            : 'Any region';
        const includedSiteTypes =
            survey.scope === 'global'
                ? [
                      survey.includeTrialSites ? 'trial' : null,
                      'subscription',
                  ]
                      .filter(Boolean)
                      .join(', ')
                : scopeLabels[survey.scope].toLowerCase();

        return (
            <div className="grid gap-1 text-xs text-muted-foreground">
                <div>Scope: {scopeLabels[survey.scope]}</div>
                <div>Included sites: {includedSiteTypes}</div>
                <div>Target: {departments}</div>
                <div>Roles: {roles}</div>
                <div>Regions: {regions}</div>
            </div>
        );
    };

    const renderEditableSurveyCard = (survey: FeedbackSurvey) => {
        const draft = surveyDrafts[survey._id] ?? {
            title: survey.title,
            description: survey.description ?? '',
            formId: survey.formId,
            startDate: survey.startDate?.slice(0, 10) ?? todayString(),
            durationDays: survey.durationDays?.toString() ?? '',
            includeTrialSites: Boolean(survey.includeTrialSites),
        };
        const status = getSurveyRunStatus(survey);
        const endDate = getEndDate(draft.startDate, draft.durationDays);
        const isEditing = editingSurveyId === survey._id;

        return (
            <Card key={survey._id} className="border-white/10 bg-muted/20">
                <CardHeader className="gap-3">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <CardTitle className="text-sm font-medium">
                                {survey.title}
                            </CardTitle>
                            <CardDescription>
                                {survey.description || 'No description'}
                            </CardDescription>
                        </div>
                        {status && (
                            <span
                                className={cn(
                                    'rounded-md px-2 py-1 text-xs font-medium',
                                    status === 'active' &&
                                        'bg-emerald-500/20 text-emerald-300',
                                    status === 'scheduled' &&
                                        'bg-amber-500/20 text-amber-300'
                                )}
                            >
                                {surveyStatusLabels[status]}
                            </span>
                        )}
                    </div>
                    <CardDescription>
                        Last run: {survey.startDate?.slice(0, 10) ?? '-'} -{' '}
                        {survey.endDate?.slice(0, 10) ?? '-'}
                    </CardDescription>
                    {renderSurveyTargetSummary(survey)}
                </CardHeader>
                <CardContent className="grid gap-4">
                    {isEditing && !survey.isDefault && (
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="grid gap-2">
                                <label className="text-xs font-medium">
                                    Name
                                </label>
                                <Input
                                    value={draft.title}
                                    onChange={(event) =>
                                        updateSurveyDraft(
                                            survey._id,
                                            'title',
                                            event.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="grid gap-2">
                                <label className="text-xs font-medium">
                                    Tally form ID
                                </label>
                                <Input
                                    value={draft.formId}
                                    onChange={(event) =>
                                        updateSurveyDraft(
                                            survey._id,
                                            'formId',
                                            event.target.value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    )}
                    {isEditing && !survey.isDefault && (
                        <div className="grid gap-2">
                            <label className="text-xs font-medium">
                                Description
                            </label>
                            <textarea
                                className="min-h-20 rounded-md border bg-background px-3 py-2 text-sm"
                                value={draft.description}
                                onChange={(event) =>
                                    updateSurveyDraft(
                                        survey._id,
                                        'description',
                                        event.target.value
                                    )
                                }
                            />
                        </div>
                    )}
                    {isEditing && (
                        <div className="grid gap-4 sm:grid-cols-3">
                            <div className="grid gap-2">
                                <label className="text-xs font-medium">
                                    Start date
                                </label>
                                <Input
                                    type="date"
                                    min={todayString()}
                                    value={draft.startDate}
                                    onChange={(event) =>
                                        updateSurveyDraft(
                                            survey._id,
                                            'startDate',
                                            event.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="grid gap-2">
                                <label className="text-xs font-medium">
                                    Duration days *
                                </label>
                                <Input
                                    type="number"
                                    min={1}
                                    required
                                    placeholder="e.g. 14"
                                    className={requiredInputClassName}
                                    value={draft.durationDays}
                                    onChange={(event) =>
                                        updateSurveyDraft(
                                            survey._id,
                                            'durationDays',
                                            event.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="grid gap-2">
                                <label className="text-xs font-medium">
                                    End date
                                </label>
                                <Input
                                    value={endDate || 'Set duration'}
                                    readOnly
                                />
                            </div>
                        </div>
                    )}
                    {isEditing &&
                        !survey.isDefault &&
                        survey.scope === 'global' && (
                            <label className="flex items-center gap-2 text-sm">
                                <Checkbox
                                    checked={draft.includeTrialSites}
                                    onCheckedChange={(checked) =>
                                        updateSurveyDraft(
                                            survey._id,
                                            'includeTrialSites',
                                            Boolean(checked)
                                        )
                                    }
                                />
                                Include trial sites
                            </label>
                    )}
                    <div className="flex flex-wrap gap-2">
                        {!isEditing && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setEditingSurveyId(survey._id)}
                            >
                                Edit
                            </Button>
                        )}
                        {isEditing && (
                            <Button
                                type="button"
                                onClick={() => handleSaveSurveyDraft(survey)}
                                disabled={savingKey === `${survey._id}:save`}
                            >
                                Save changes
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleDeactivateSurvey(survey)}
                            disabled={
                                savingKey === `${survey._id}:deactivate`
                            }
                        >
                            Deactivate
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    };

    const renderCustomOverview = () => (
        <div className="grid gap-5">
            <div className="grid gap-3 lg:grid-cols-3">
                <div className="grid gap-2">
                    <label className="text-xs font-medium">Department</label>
                    <Combobox
                        options={[
                            { value: 'all', label: 'All departments' },
                            ...departmentOptions,
                        ]}
                        value={overviewDepartmentFilter}
                        onChange={setOverviewDepartmentFilter}
                        placeholder="Search department"
                        emptyText="No departments found."
                    />
                </div>
                <div className="grid gap-2">
                    <label className="text-xs font-medium">Role</label>
                    <Combobox
                        options={[
                            { value: 'all', label: 'All roles' },
                            ...roleOptions,
                        ]}
                        value={overviewRoleFilter}
                        onChange={setOverviewRoleFilter}
                        placeholder="Search role"
                        emptyText="No roles found."
                    />
                </div>
                <div className="grid gap-2">
                    <label className="text-xs font-medium">Region</label>
                    <Combobox
                        options={[
                            { value: 'all', label: 'All regions' },
                            ...regionOptions,
                        ]}
                        value={overviewRegionFilter}
                        onChange={setOverviewRegionFilter}
                        placeholder="Search region"
                        emptyText="No regions found."
                    />
                </div>
            </div>
            <div className="max-h-[56vh] overflow-auto pr-2">
                <div className="grid gap-4">
                    {filteredCustomSurveys.length > 0 ? (
                        filteredCustomSurveys.map(renderEditableSurveyCard)
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            No active custom surveys match the filters.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );

    const renderTrialOverview = () => {
        const selectedDepartmentSurveys = activeTrialSurveys.filter(
            (survey) =>
                getPrimarySurveyDepartmentId(survey) === overviewTrialDepartmentId
        );

        return (
            <div className="grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
                <div className="max-h-[56vh] overflow-auto pr-2">
                    <div className="grid gap-2">
                        {activeTrialDepartments.length > 0 ? (
                            activeTrialDepartments.map((department) => (
                                <Button
                                    key={department._id}
                                    type="button"
                                    variant={
                                        overviewTrialDepartmentId ===
                                        department._id
                                            ? 'default'
                                            : 'outline'
                                    }
                                    className="justify-start"
                                    onClick={() =>
                                        setOverviewTrialDepartmentId(
                                            department._id
                                        )
                                    }
                                >
                                    {department.name}
                                </Button>
                            ))
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                No departments have active trial surveys.
                            </p>
                        )}
                    </div>
                </div>
                <div className="max-h-[56vh] overflow-auto pr-2">
                    <div className="grid gap-4">
                        {overviewTrialDepartmentId ? (
                            selectedDepartmentSurveys.map(
                                renderEditableSurveyCard
                            )
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                Select a department to see its active trial
                                surveys.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderActiveSurveysOverview = () => (
        <Card className="border-white/10">
            <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                    <CardTitle className="text-base font-medium">
                        Active surveys
                    </CardTitle>
                    <CardDescription>
                        Review, edit, and deactivate active survey
                        configurations.
                    </CardDescription>
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={closePanel}
                >
                    <X />
                </Button>
            </CardHeader>
            <CardContent className="grid gap-5">
                <div className="flex flex-wrap gap-2">
                    <Button
                        type="button"
                        variant={overviewTab === 'trial' ? 'default' : 'outline'}
                        onClick={() => setOverviewTab('trial')}
                    >
                        Trial surveys
                    </Button>
                    <Button
                        type="button"
                        variant={overviewTab === 'custom' ? 'default' : 'outline'}
                        onClick={() => setOverviewTab('custom')}
                    >
                        Custom surveys
                    </Button>
                </div>
                {overviewTab === 'trial' && renderTrialOverview()}
                {overviewTab === 'custom' && renderCustomOverview()}
            </CardContent>
        </Card>
    );

    const renderTrialSurveyForm = () => (
        <Card className="border-white/10">
            <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                    <CardTitle className="text-base font-medium">
                        Add trial survey
                    </CardTitle>
                    <CardDescription>
                        Activate a default Tally survey for a selected trial
                        department.
                    </CardDescription>
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={closePanel}>
                    <X />
                </Button>
            </CardHeader>
            <CardContent className="grid gap-5">
                <div className="grid gap-2">
                    <label className="text-xs font-medium">Department</label>
                    <Combobox
                        {...surveyComboboxProps}
                        options={trialDepartmentOptions}
                        value={trialDepartmentId}
                        onChange={setTrialDepartmentId}
                        placeholder="Search department"
                        emptyText="No trial departments found."
                    />
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                    {(options?.defaultTemplates ?? []).map((template) => {
                        const setup = trialTemplateSetup[
                            template.templateKey
                        ] ?? {
                            startDate: todayString(),
                            durationDays: '',
                        };
                        const endDate = getEndDate(
                            setup.startDate,
                            setup.durationDays
                        );
                        const latestSurvey = getLatestTrialSurvey(
                            template.templateKey
                        );
                        const status = getSurveyRunStatus(latestSurvey);
                        const canEditExisting =
                            status === 'active' || status === 'scheduled';

                        return (
                            <Card
                                key={template.templateKey}
                                className={cn(
                                    'border-white/10 bg-muted/20',
                                    status === 'active' &&
                                        'border-emerald-500/60 bg-emerald-500/10',
                                    status === 'scheduled' &&
                                        'border-amber-500/60 bg-amber-500/10',
                                    status === 'expired' &&
                                        'border-slate-500/60 bg-slate-500/10'
                                )}
                            >
                                <CardHeader className="gap-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <CardTitle className="text-sm font-medium">
                                                {template.title}
                                            </CardTitle>
                                            <CardDescription>
                                                {template.description}
                                            </CardDescription>
                                        </div>
                                        {status && (
                                            <span
                                                className={cn(
                                                    'rounded-md px-2 py-1 text-xs font-medium',
                                                    status === 'active' &&
                                                        'bg-emerald-500/20 text-emerald-300',
                                                    status === 'scheduled' &&
                                                        'bg-amber-500/20 text-amber-300',
                                                    status === 'expired' &&
                                                        'bg-slate-500/20 text-slate-300',
                                                    status === 'inactive' &&
                                                        'bg-muted text-muted-foreground'
                                                )}
                                            >
                                                {surveyStatusLabels[status]}
                                            </span>
                                        )}
                                    </div>
                                    {latestSurvey && (
                                        <CardDescription>
                                            Last run:{' '}
                                            {latestSurvey.startDate?.slice(
                                                0,
                                                10
                                            ) ?? '-'}{' '}
                                            -{' '}
                                            {latestSurvey.endDate?.slice(
                                                0,
                                                10
                                            ) ?? '-'}
                                        </CardDescription>
                                    )}
                                </CardHeader>
                                <CardContent className="grid gap-4">
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <div className="grid gap-2">
                                            <label className="text-xs font-medium">
                                                Start date
                                            </label>
                                            <Input
                                                type="date"
                                                min={todayString()}
                                                value={setup.startDate}
                                                onChange={(event) =>
                                                    updateTrialTemplateSetup(
                                                        template.templateKey,
                                                        'startDate',
                                                        event.target.value
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="grid gap-2">
                                            <label className="text-xs font-medium">
                                                Duration days *
                                            </label>
                                            <Input
                                                type="number"
                                                min={1}
                                                required
                                                placeholder="e.g. 14"
                                                className={
                                                    requiredInputClassName
                                                }
                                                value={setup.durationDays}
                                                onChange={(event) =>
                                                    updateTrialTemplateSetup(
                                                        template.templateKey,
                                                        'durationDays',
                                                        event.target.value
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        End date: {endDate || 'Set duration'}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            type="button"
                                            onClick={() =>
                                                handleActivateTrialSurvey(
                                                    template
                                                )
                                            }
                                            disabled={
                                                loadingOptions ||
                                                savingKey ===
                                                    template.templateKey
                                            }
                                        >
                                            <CalendarDays />
                                            {canEditExisting
                                                ? 'Save changes'
                                                : status === 'expired'
                                                ? 'Activate again'
                                                : 'Activate'}
                                        </Button>
                                        {latestSurvey && canEditExisting && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() =>
                                                    handleDeactivateTrialSurvey(
                                                        latestSurvey
                                                    )
                                                }
                                                disabled={
                                                    loadingOptions ||
                                                    savingKey ===
                                                        `${latestSurvey._id}:deactivate`
                                                }
                                            >
                                                Deactivate
                                            </Button>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );

    const renderCustomSurveyForm = () => (
        <Card className="border-white/10">
            <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                    <CardTitle className="text-base font-medium">
                        Add survey
                    </CardTitle>
                    <CardDescription>
                        Configure the target and Tally survey.
                    </CardDescription>
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={closePanel}>
                    <X />
                </Button>
            </CardHeader>
            <CardContent className="grid gap-5">
                <div className="grid gap-4 rounded-md border p-4">
                    <div>
                        <div className="text-sm font-medium">Targeting</div>
                        <p className="text-xs text-muted-foreground">
                            Use departments, roles, and regions to define who
                            should receive this survey.
                        </p>
                    </div>
                    <div className="grid gap-4 lg:grid-cols-3">
                        {renderDepartmentDropdown()}
                        {renderRoleDropdown()}
                        {renderRegionDropdown()}
                    </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="grid gap-2">
                        <label className="text-xs font-medium">Name</label>
                        <Input
                            value={customTitle}
                            onChange={(event) =>
                                setCustomTitle(event.target.value)
                            }
                            placeholder="Survey name"
                        />
                    </div>
                    <div className="grid gap-2">
                        <label className="text-xs font-medium">
                            Tally form ID
                        </label>
                        <Input
                            value={customFormId}
                            onChange={(event) =>
                                setCustomFormId(event.target.value)
                            }
                            placeholder="abc123"
                        />
                    </div>
                </div>

                <div className="grid gap-2">
                    <label className="text-xs font-medium">Description</label>
                    <textarea
                        className="min-h-24 rounded-md border bg-background px-3 py-2 text-sm"
                        value={customDescription}
                        onChange={(event) =>
                            setCustomDescription(event.target.value)
                        }
                        placeholder="Optional survey description"
                    />
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                    <div className="grid gap-2">
                        <label className="text-xs font-medium">
                            Start date
                        </label>
                        <Input
                            type="date"
                            min={todayString()}
                            value={customStartDate}
                            onChange={(event) =>
                                setCustomStartDate(event.target.value)
                            }
                        />
                    </div>
                    <div className="grid gap-2">
                        <label className="text-xs font-medium">
                            Duration days *
                        </label>
                        <Input
                            type="number"
                            min={1}
                            required
                            placeholder="e.g. 30"
                            className={requiredInputClassName}
                            value={customDurationDays}
                            onChange={(event) =>
                                setCustomDurationDays(event.target.value)
                            }
                        />
                    </div>
                    <div className="grid gap-2">
                        <label className="text-xs font-medium">End date</label>
                        <Input
                            value={
                                getEndDate(
                                    customStartDate,
                                    customDurationDays
                                ) || 'Set duration'
                            }
                            readOnly
                        />
                    </div>
                </div>

                <Button
                    type="button"
                    onClick={handleCreateCustomSurvey}
                    disabled={savingKey === 'custom' || loadingOptions}
                >
                    <Globe2 />
                    Create survey
                </Button>
            </CardContent>
        </Card>
    );

    return (
        <div className="grid gap-6">
            <div className="grid gap-4 sm:grid-cols-3">
                <Button
                    type="button"
                    variant="outline"
                    className="h-12 justify-start"
                    onClick={() => setMode('trial')}
                >
                    <ClipboardPlus />
                    Add trial survey
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    className="h-12 justify-start"
                    onClick={() => setMode('custom')}
                >
                    <Globe2 />
                    Add survey
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    className="h-12 justify-start"
                    onClick={() => setMode('overview')}
                >
                    <ListChecks />
                    Active surveys
                </Button>
            </div>

            <Dialog open={mode !== null}>
                <DialogContent
                    className="max-h-[90vh] max-w-6xl overflow-auto p-0"
                    hideCloseButton
                    onEscapeKeyDown={(event) => event.preventDefault()}
                    onInteractOutside={(event) => event.preventDefault()}
                >
                    <DialogHeader className="sr-only">
                        <DialogTitle>
                            {mode === 'trial'
                                ? 'Add trial survey'
                                : mode === 'custom'
                                ? 'Add survey'
                                : 'Active surveys'}
                        </DialogTitle>
                        <DialogDescription>
                            Create and activate feedback surveys.
                        </DialogDescription>
                    </DialogHeader>
                    {mode === 'trial' && renderTrialSurveyForm()}
                    {mode === 'custom' && renderCustomSurveyForm()}
                    {mode === 'overview' && renderActiveSurveysOverview()}
                </DialogContent>
            </Dialog>
            <AlertDialog
                open={pendingDeleteSurvey !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setPendingDeleteSurvey(null);
                    }
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete survey?</AlertDialogTitle>
                        <AlertDialogDescription>
                            If you deactivate this survey, it will be deleted
                            and will no longer be shown to the selected target.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmDeleteSurvey}
                            disabled={
                                pendingDeleteSurvey
                                    ? savingKey ===
                                      `${pendingDeleteSurvey._id}:deactivate`
                                    : false
                            }
                        >
                            Delete survey
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <AlertDialog
                open={pendingOverlapAction !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setPendingOverlapAction(null);
                    }
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Survey date overlap</AlertDialogTitle>
                        <AlertDialogDescription>
                            The selected date range overlaps with existing
                            active or scheduled surveys for one or more
                            departments. You can adjust the setup or proceed
                            anyway.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="max-h-72 overflow-auto rounded-md border p-3 text-sm">
                        <div className="grid gap-3">
                            {pendingOverlapAction?.overlap.departments.map(
                                (department) => (
                                    <div key={department._id}>
                                        <div className="font-medium">
                                            {department.name}
                                        </div>
                                        <ul className="mt-1 list-inside list-disc text-muted-foreground">
                                            {department.surveys.map(
                                                (survey) => (
                                                    <li key={survey._id}>
                                                        {survey.title} (
                                                        {formatDate(
                                                            survey.startDate
                                                        )}{' '}
                                                        -{' '}
                                                        {formatDate(
                                                            survey.endDate
                                                        )}
                                                        )
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel and adjust</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleProceedWithOverlap}
                            disabled={savingKey === 'overlap'}
                        >
                            Proceed anyway
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default FeedbackSurveySetup;
